import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import { Form, Upload } from 'antd';
import { Document as PdfDocument, Page as PdfPage, pdfjs } from 'react-pdf';

import { AddCircle, CloudUpload, Document, Trash } from '../../../assets/icons';
import { FILE_UPLOAD_VALIDATION } from '../../../utils/fileValidation';

pdfjs.GlobalWorkerOptions.workerSrc = new URL('pdfjs-dist/build/pdf.worker.min.mjs', import.meta.url).toString();

// Cap on the scrollable multi-page PDF preview's height (px) — pages stack vertically
// inside this and the box scrolls once their combined height exceeds it.
const PDF_PREVIEW_MAX_HEIGHT = 400;

const PREVIEW_BOX_BASE_STYLE = {
  border: '1px solid #DCD8D7',
  borderRadius: 8,
  boxSizing: 'border-box',
  backgroundColor: '#FBFAF8',
};

const PDF_PREVIEW_STYLE = {
  display: 'block',
  overflowY: 'auto',
  maxHeight: PDF_PREVIEW_MAX_HEIGHT,
  height: 'auto',
  padding: 8,
  ...PREVIEW_BOX_BASE_STYLE,
};

const FILE_PREVIEW_FALLBACK_STYLE = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 8,
  padding: 16,
  minHeight: 166,
  ...PREVIEW_BOX_BASE_STYLE,
};

const getVisualPreviewUrl = ({ isPreviewableImage, isPdf, previewUrl, pdfPreviewUrl }) => {
  if (isPreviewableImage) return previewUrl;
  if (isPdf) return pdfPreviewUrl;
  return null;
};

const PdfPreview = ({ file, numPages, pageWidth, onLoadSuccess, onLoadError, onFirstPageRenderSuccess }) => {
  const pageNumbers = Array.from({ length: numPages || 1 }, (_, index) => index + 1);

  return (
    <PdfDocument file={file} loading={<Document size={40} color='#8A4743' />} onLoadSuccess={onLoadSuccess} onLoadError={onLoadError}>
      {pageNumbers.map((pageNumber) => (
        <div key={pageNumber} style={{ marginBottom: pageNumber < pageNumbers.length ? 8 : 0 }}>
          <PdfPage
            pageNumber={pageNumber}
            width={pageWidth}
            renderTextLayer={false}
            renderAnnotationLayer={false}
            onRenderSuccess={pageNumber === 1 ? onFirstPageRenderSuccess : undefined}
          />
        </div>
      ))}
    </PdfDocument>
  );
};

PdfPreview.propTypes = {
  file: PropTypes.object,
  numPages: PropTypes.number,
  pageWidth: PropTypes.number,
  onLoadSuccess: PropTypes.func,
  onLoadError: PropTypes.func,
  onFirstPageRenderSuccess: PropTypes.func,
};

// Preview shown in place of the dropzone once a file is picked: the image itself, a
// scrollable multi-page PDF render, or a generic document icon + filename fallback.
const DropzonePreview = ({
  containerRef,
  file,
  fileName,
  previewUrl,
  showPdfPreview,
  pdfNumPages,
  pdfPageWidth,
  onPdfLoadSuccess,
  onPdfLoadError,
  onFirstPageRenderSuccess,
}) => {
  if (previewUrl) {
    return (
      <div ref={containerRef} className='dropzone-uploader__preview'>
        <img src={previewUrl} alt={fileName} className='dropzone-uploader__preview-image' />
      </div>
    );
  }

  return (
    <div ref={containerRef} className='dropzone-uploader__preview' style={showPdfPreview ? PDF_PREVIEW_STYLE : FILE_PREVIEW_FALLBACK_STYLE}>
      {showPdfPreview ? (
        <PdfPreview
          file={file}
          numPages={pdfNumPages}
          pageWidth={pdfPageWidth}
          onLoadSuccess={onPdfLoadSuccess}
          onLoadError={onPdfLoadError}
          onFirstPageRenderSuccess={onFirstPageRenderSuccess}
        />
      ) : (
        <>
          <Document size={40} color='#8A4743' />
          <span style={{ fontSize: 13, color: '#726460', wordBreak: 'break-word', textAlign: 'center', maxWidth: '90%' }}>{fileName}</span>
        </>
      )}
    </div>
  );
};

DropzonePreview.propTypes = {
  containerRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ current: PropTypes.any })]),
  file: PropTypes.object,
  fileName: PropTypes.string,
  previewUrl: PropTypes.string,
  showPdfPreview: PropTypes.bool,
  pdfNumPages: PropTypes.number,
  pdfPageWidth: PropTypes.number,
  onPdfLoadSuccess: PropTypes.func,
  onPdfLoadError: PropTypes.func,
  onFirstPageRenderSuccess: PropTypes.func,
};

export const UploadField = ({
  className,
  onChangeHandle,
  fileList,
  handleRemoveUploaded,
  id,
  beforeUpload,
  label,
  fileValidate = false,
  fileSizeValidate = false,
  passwordProtectedValidate = false,
  loading = false,
  accept = 'image/png, image/jpeg, image/jpg',
  validationMessages = FILE_UPLOAD_VALIDATION,
  variant = 'picture-card',
  hint,
  multiple = false,
}) => {
  const isDropzone = variant === 'dropzone';
  const uploadedFile = fileList[0]?.originFileObj;
  const isPreviewableImage = !!uploadedFile?.type?.startsWith('image/');
  const isPdf = uploadedFile?.type === 'application/pdf';
  const [previewUrl, setPreviewUrl] = useState(null);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState(null);
  const [pdfNumPages, setPdfNumPages] = useState(null);
  const [pdfLoadFailed, setPdfLoadFailed] = useState(false);
  const [pdfPageWidth, setPdfPageWidth] = useState(240);
  const pdfContainerRef = useRef(null);

  useEffect(() => {
    if (!uploadedFile || !isPreviewableImage) {
      setPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(uploadedFile);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [uploadedFile, isPreviewableImage]);

  useEffect(() => {
    setPdfPreviewUrl(null);
    setPdfNumPages(null);
    setPdfLoadFailed(false);
  }, [uploadedFile]);

  useEffect(() => {
    if (!isPdf) return;
    const measure = () => {
      const width = pdfContainerRef.current?.clientWidth;
      if (width) setPdfPageWidth(width);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [isPdf]);

  // The small filename-row thumbnail reuses page 1's canvas from this same visible
  // multi-page render, rather than rendering the PDF a second time off-screen.
  const handleFirstPageRenderSuccess = () => {
    const canvas = pdfContainerRef.current?.querySelector('canvas');
    if (!canvas) return;
    try {
      setPdfPreviewUrl(canvas.toDataURL());
    } catch {
      setPdfPreviewUrl(null);
    }
  };

  const visualPreviewUrl = getVisualPreviewUrl({ isPreviewableImage, isPdf, previewUrl, pdfPreviewUrl });

  let uploader;
  if (isDropzone && fileList.length < 1) {
    uploader = (
      <Upload.Dragger
        id={id}
        accept={accept}
        onChange={onChangeHandle}
        showUploadList={false}
        fileList={fileList}
        className='dropzone-uploader'
        beforeUpload={beforeUpload}
        disabled={loading}
        multiple={multiple}
      >
        <div className='dropzone-uploader__content'>
          <CloudUpload />
          <p className='fw-600 dropzone-uploader__title'>{label}</p>
          <p className='dropzone-uploader__hint-action'>
            Drag and drop files here, or <span className='dropzone-uploader__browse'>browse files</span>
          </p>
          {hint && <p className='dropzone-uploader__hint'>{hint}</p>}
        </div>
      </Upload.Dragger>
    );
  } else if (isDropzone) {
    uploader = (
      <DropzonePreview
        containerRef={pdfContainerRef}
        file={uploadedFile}
        fileName={fileList[0]?.name}
        previewUrl={isPreviewableImage ? previewUrl : null}
        showPdfPreview={isPdf && !pdfLoadFailed}
        pdfNumPages={pdfNumPages}
        pdfPageWidth={pdfPageWidth}
        onPdfLoadSuccess={({ numPages }) => setPdfNumPages(numPages)}
        onPdfLoadError={() => setPdfLoadFailed(true)}
        onFirstPageRenderSuccess={handleFirstPageRenderSuccess}
      />
    );
  } else {
    uploader = (
      <Upload
        id={id}
        accept={accept}
        onChange={onChangeHandle}
        showUploadList={{
          showRemoveIcon: false,
          showPreviewIcon: false,
        }}
        fileList={fileList}
        className='avatar-uploader'
        listType='picture-card'
        beforeUpload={beforeUpload}
        disabled={loading}
        multiple={multiple}
      >
        {fileList.length < 1 && (
          <div>
            <AddCircle />
            <p className='fw-600'>{label}</p>
          </div>
        )}
      </Upload>
    );
  }

  return (
    <Form.Item className={className}>
      {uploader}
      {fileList[0] && (
        <div className='filename'>
          <span>
            {visualPreviewUrl ? (
              <img
                src={visualPreviewUrl}
                alt={fileList[0].name}
                className='filename__thumbnail'
                style={{ width: 24, height: 24, objectFit: 'cover', borderRadius: 4, verticalAlign: 'middle', marginRight: 7 }}
              />
            ) : (
              <Document size={24} verticalAlign='middle' marginRight='7px' />
            )}
            {fileList[0].name}
          </span>
          <Trash verticalAlign='middle' className='delete-icon' onClick={() => handleRemoveUploaded(fileList[0].uid, 'front')} />
        </div>
      )}
      {fileValidate === true && (
        <p className='error' style={{ color: 'red', paddingTop: 10 }}>
          {validationMessages.FORMAT}
        </p>
      )}
      {fileSizeValidate === true && <p style={{ color: 'red', paddingTop: 10 }}>{validationMessages.FILE_SIZE}</p>}
      {passwordProtectedValidate === true && <p style={{ color: 'red', paddingTop: 10 }}>{validationMessages.PASSWORD_PROTECTED}</p>}
    </Form.Item>
  );
};

UploadField.propTypes = {
  className: PropTypes.string,
  onChangeHandle: PropTypes.func,
  fileList: PropTypes.arrayOf(
    PropTypes.shape({
      uid: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string,
      originFileObj: PropTypes.object,
    })
  ).isRequired,
  handleRemoveUploaded: PropTypes.func,
  id: PropTypes.string,
  beforeUpload: PropTypes.func,
  label: PropTypes.node,
  fileValidate: PropTypes.bool,
  fileSizeValidate: PropTypes.bool,
  passwordProtectedValidate: PropTypes.bool,
  loading: PropTypes.bool,
  accept: PropTypes.string,
  validationMessages: PropTypes.shape({
    FORMAT: PropTypes.node,
    FILE_SIZE: PropTypes.node,
    PASSWORD_PROTECTED: PropTypes.node,
  }),
  variant: PropTypes.oneOf(['picture-card', 'dropzone']),
  hint: PropTypes.node,
  multiple: PropTypes.bool,
};
